import { useEffect, useRef } from 'react';
import { campaignsAPI } from '../services/api';

/**
 * Hook personalizado para polling de progreso de campañas
 * Actualiza el progreso en tiempo real y notifica cuando se completan/quedan incompletas
 */
export const useCampaignProgress = (activeTab, importingFile, setCampaignsList) => {
  const previousCampaignSnapshotRef = useRef(new Map());
  const hasInitializedProgressRef = useRef(false);

  useEffect(() => {
    if (activeTab !== 'campaigns') return;

    let intervalId;

    const tick = async () => {
      try {
        // PRIMERO sincronizar el progreso desde message_logs
        try {
          await fetch('/sistemasms/backend/api/campaigns/sync-campaign-progress.php', {
            method: 'POST',
            credentials: 'include'
          });
          console.log('✅ [Hook] Sincronización ejecutada');
        } catch (syncError) {
          console.error('❌ [Hook] Error en sincronización:', syncError);
        }

        // LUEGO obtener campañas y progreso en vivo (anti adelantado)
        const [listResp, liveResp] = await Promise.all([
          campaignsAPI.list({ userId: 1 }),
          fetch('/sistemasms/backend/api/campaigns/get-progress.php', { credentials: 'include' }).then(r => r.json())
        ]);
        const campaigns = listResp?.data?.data?.campaigns || [];
        const liveRows = (liveResp?.data?.progress || []).reduce((map, row) => {
          map[row.campaign_id] = row;
          return map;
        }, {});

        console.log('🔄 [Hook] Polling ejecutado:', {
          totalCampaigns: campaigns.length,
          importingFile,
          initialized: hasInitializedProgressRef.current,
          liveRowsCount: Object.keys(liveRows).length
        });

        // Debug: mostrar datos de campañas
        campaigns.forEach(c => {
          const live = liveRows[c.id];
          console.log(`📋 [Hook] Campaña ${c.id}: status=${c.status}, messages_sent=${c.messages_sent}, live.processed=${live?.processed || 0}`);
        });

        // Construir dataset unificado (lo que verá la UI y lo que usan notificaciones)
        const merged = campaigns.map(c => {
          console.log(`🔍 [Hook] Procesando campaña ${c.id}: ${c.campaign_name}, status: ${c.status}, messages_sent: ${c.messages_sent}`);

          // Para campañas en 'draft': siempre 0
          if (c.status === 'draft') {
            return { ...c, messages_sent: 0 };
          }

          // Para campañas en 'sending': usar conteo en vivo (debe ser preciso ahora)
          if (c.status === 'sending') {
            const live = liveRows[c.id];
            const processed = live ? Math.min(parseInt(live.processed || 0), parseInt(c.total_recipients || 0)) : 0;
            console.log(`📊 [Hook] Campaña ${c.id} en sending: live.processed=${live?.processed || 0}, usando ${processed}/${c.total_recipients}`);
            return { ...c, messages_sent: processed };
          }

          // Para campañas completadas o en otros estados, usar el valor de la BD
          return c;
        });

        // Primer tick: solo inicializar snapshots con merged, sin notificaciones
        if (!hasInitializedProgressRef.current) {
          merged.forEach((c) => {
            previousCampaignSnapshotRef.current.set(c.id, {
              status: c.status,
              messages_sent: c.messages_sent,
              total_recipients: c.total_recipients
            });
          });
          setCampaignsList(merged);
          hasInitializedProgressRef.current = true;
          console.log('✅ [Hook] Inicializado con', campaigns.length, 'campañas');
          return;
        }

        // SIEMPRE actualizar la UI con progreso en vivo si está disponible
        setCampaignsList(merged);

        // Solo notificar si NO está importando
        if (!importingFile) {
          merged.forEach((c) => {
            const prev = previousCampaignSnapshotRef.current.get(c.id);
            if (!prev) {
              // Nueva campaña detectada, agregar al snapshot
              previousCampaignSnapshotRef.current.set(c.id, {
                status: c.status,
                messages_sent: c.messages_sent,
                total_recipients: c.total_recipients
              });
              return;
            }

            // Resetear progreso si campaña acaba de pasar a 'sending' (nueva ejecución)
            if (prev.status !== 'sending' && c.status === 'sending') {
              console.log('🚀 [Hook] Campaña inició envío, reseteando progreso:', c.campaign_name);
              previousCampaignSnapshotRef.current.set(c.id, {
                status: c.status,
                messages_sent: 0, // Resetear a 0 cuando inicia envío real
                total_recipients: c.total_recipients
              });
              return; // No notificar en este tick
            }

            // Si campaña está en 'sending' pero el conteo en vivo es 0, mantener 0
            if (c.status === 'sending' && (c.messages_sent || 0) === 0) {
              console.log('⏳ [Hook] Campaña en sending pero sin progreso aún:', c.campaign_name);
              previousCampaignSnapshotRef.current.set(c.id, {
                status: c.status,
                messages_sent: 0,
                total_recipients: c.total_recipients
              });
              return; // No notificar hasta que haya progreso real
            }

            // Notificar completada cuando realmente termina (solo si pasa de <total a >=total)
            const progresoPrevio = prev.messages_sent || 0;
            const progresoActual = c.messages_sent || 0;

            if (
              c.total_recipients > 0 &&
              progresoPrevio < c.total_recipients &&
              progresoActual >= c.total_recipients &&
              c.status === 'completed' // Solo si realmente está completada
            ) {
              console.log('🎉 [Hook] Campaña completada detectada:', c.campaign_name, `${progresoPrevio}/${c.total_recipients} → ${progresoActual}/${c.total_recipients}`);
              alert(`🎉 Campaña completada\n\n${c.campaign_name}: ${c.messages_sent}/${c.total_recipients}`);
            }

            // Notificar incompleta: sale de sending y queda faltante
            const leftSending = prev.status === 'sending' && c.status !== 'sending';
            if (leftSending && c.total_recipients > 0 && c.messages_sent < c.total_recipients) {
              console.log('⚠️ [Hook] Campaña incompleta detectada:', c.campaign_name);
              alert(`⚠️ Campaña incompleta\n\n${c.campaign_name}: ${c.messages_sent}/${c.total_recipients}`);
            }

            // Actualizar snapshot
            previousCampaignSnapshotRef.current.set(c.id, {
              status: c.status,
              messages_sent: c.messages_sent,
              total_recipients: c.total_recipients
            });
          });
        }
      } catch (error) {
        console.error('❌ [Hook] Error actualizando progreso:', error);
      }
    };

    // Primer tick inmediato y luego cada 4s
    tick();
    intervalId = setInterval(tick, 4000);

    return () => clearInterval(intervalId);
  }, [activeTab, importingFile, setCampaignsList]);
};

